import { FC } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import $api from "../../shared/api/axios";
import { AppLoader } from "../../shared/components/AppLoader";
import { getRoutePrompts, getRoutePromptsRevision, getRoutePromptTestCases } from "../../shared/const/router";
import { RevisionHistory } from "./RevisionHistory";
import { TActiveRevision, formatRevisionLabel } from "./revision";

type TFeatureMeta = {
  featureKey: string;
  label: string;
  promptCount: number;
  activeRevision: TActiveRevision;
  supportsTests: boolean;
};

type TFeaturePromptsResponse = {
  activeRevision: TActiveRevision;
  pinnedRevisionId: number | null;
  pinnedRevisionNumber: number | null;
};

export const FeaturePromptsPage: FC = () => {
  const { featureKey = "" } = useParams<{ featureKey: string }>();

  const { data: features = [] } = useQuery<TFeatureMeta[]>({
    queryKey: ["admin-prompt-features"],
    queryFn: async () => {
      const response = await $api.get("/admin/prompts/features");
      return response?.data || [];
    }
  });

  const feature = features.find((f) => f.featureKey === featureKey);
  const featureLabel = feature?.label ?? featureKey;

  const { data, isLoading, isError } = useQuery<TFeaturePromptsResponse>({
    queryKey: ["admin-prompts", featureKey],
    queryFn: async () => {
      const response = await $api.get(`/admin/prompts/features/${featureKey}`);
      return response.data;
    },
    enabled: !!featureKey
  });

  const renderContent = () => {
    if (isLoading) return <AppLoader />;
    if (isError || !data) {
      return <div className="p-8 text-center text-red-600">Failed to load prompts.</div>;
    }
    const activeCardContent = (
      <>
        <FigmaTypography component="div" variant="body-2" className="text-gray-900">
          {formatRevisionLabel(data.activeRevision)}
        </FigmaTypography>
        {data.activeRevision?.note && (
          <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-1">
            {data.activeRevision.note}
          </FigmaTypography>
        )}
      </>
    );

    return (
      <>
        {data.activeRevision ? (
          <Link
            to={getRoutePromptsRevision(featureKey, data.activeRevision.id)}
            className="mb-6 block rounded-lg border bg-card px-6 py-4 hover:bg-gray-50"
          >
            {activeCardContent}
          </Link>
        ) : (
          <div className="mb-6 rounded-lg border bg-card px-6 py-4">{activeCardContent}</div>
        )}

        {data.pinnedRevisionId && (
          <div className="mb-6 rounded-lg border border-purple-200 bg-purple-50 px-6 py-3">
            <FigmaTypography component="div" variant="body-3" className="text-purple-800">
              Your own generations run on revision{" "}
              <Link to={getRoutePromptsRevision(featureKey, data.pinnedRevisionId)} className="underline">
                {data.pinnedRevisionNumber ?? data.pinnedRevisionId}
              </Link>
              , not the active one.
            </FigmaTypography>
          </div>
        )}

        <div className="mb-6">
          <RevisionHistory featureKey={featureKey} />
        </div>
      </>
    );
  };

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-6">
        <Link to={getRoutePrompts()} className="text-[14px] text-gray-500 hover:text-gray-700">
          ← All features
        </Link>
        <div className="mt-2 flex items-center justify-between gap-4">
          <FigmaTypography component="h1" variant="heading-1">{featureLabel}</FigmaTypography>
          {feature?.supportsTests && (
            <Link to={getRoutePromptTestCases(featureKey)} className="text-[14px] text-gray-500 hover:text-gray-700">
              Test cases →
            </Link>
          )}
        </div>
      </div>

      {renderContent()}
    </div>
  );
};
